import { useMemo, useState } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import BankingCard from '@apigrowfinance/pbolt-banking-card';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { CustomTheme, useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import pismo from '@assets/img/pismo.png';
import { useAccount } from '@contexts/AccountProvider';
import cardAltText from '@messages/cardAltText';
import getEnumTranslation from '@utils/getEnumTranslation';

const CardLinkPanel = () => {
    const { account, program, isLoading } = useAccount();
    const { theme } = useTheme();
    const { formatMessage } = useIntl();
    const [isHovered, setIsHovered] = useState(false);
    const navigate = useNavigate();

    const ariaLabel = useMemo(
        () =>
            formatMessage({
                id: 'CardLinkPanel.ariaLabel',
                defaultMessage: 'View cards',
            }),
        [formatMessage],
    );

    // TODO: get the customer ID from the account context
    const viewCards = () => navigate(`/${account?.account_id}/cards`);

    const panelTitle = useMemo(() => {
        return program.type === 'CREDITO' ? (
            <FormattedMessage
                id="CardLinkPanel.creditCards"
                defaultMessage="Credit cards"
            />
        ) : (
            <FormattedMessage
                id="CardLinkPanel.debitCards"
                defaultMessage="Debit cards"
            />
        );
    }, [program?.type]);

    return (
        <Panel
            onClick={isLoading ? null : viewCards}
            isHovered={setIsHovered}
            ariaLabel={ariaLabel}
            evenSidePadding={false}>
            <PanelTitle
                hovered={isHovered}
                addPadding
                arrowLabel={getEnumTranslation('viewAll')}
            />

            <PanelContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            mt: -4.5,
                        }}>
                        <Typography
                            variant="overline"
                            data-testid="card-link-panel-header">
                            {isLoading ? (
                                <Skeleton
                                    width={theme.spacing(11)}
                                    data-testid="card-link-panel-header-loading"
                                />
                            ) : (
                                panelTitle
                            )}
                        </Typography>
                        <Typography
                            variant="h2"
                            data-testid="card-link-panel-main"
                            sx={(theme: CustomTheme) => ({
                                color: theme.custom.brand.textMain,
                                fontWeight: 600,
                            })}>
                            {isLoading ? (
                                <Skeleton
                                    width={theme.spacing(11)}
                                    data-testid="card-link-panel-main-loading"
                                />
                            ) : (
                                <FormattedMessage
                                    id="CardLinkPanel.cards"
                                    defaultMessage="Cards"
                                />
                            )}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            overflow: 'hidden',
                            borderBottomRightRadius: (theme) =>
                                theme.shape.borderRadius,
                            mb: -2.5,
                        }}>
                        <Box
                            sx={{
                                maxWidth: '224px',
                                maxHeight: '131px',
                            }}>
                            <BankingCard
                                isLoading={isLoading}
                                last4Digits="1234"
                                expiryDate="2022-01-01T07:53:35Z"
                                printedName="Customer"
                                fontScale={0.75}
                                foregroundLogo={{
                                    image:
                                        theme?.custom?.brand?.brandImage ||
                                        pismo,
                                    altText:
                                        theme?.custom?.brand?.brandName ||
                                        getEnumTranslation(
                                            'cardForegroundAltText',
                                            {
                                                messages: cardAltText,
                                                formatMessage,
                                            },
                                        ),
                                }}
                                backgroundLogo={{
                                    image: theme?.custom?.brand
                                        ?.brandImageSmall,
                                    altText:
                                        theme?.custom?.brand?.brandName ||
                                        getEnumTranslation(
                                            'cardBackgroundAltText',
                                            {
                                                messages: cardAltText,
                                                formatMessage,
                                            },
                                        ),
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </PanelContent>
        </Panel>
    );
};

export default CardLinkPanel;
